import React, { FC } from 'react';

import { useTheme } from '../../util/theme';

import { getClassNames } from './Block.styles';
import { BlockProps, TextWrapStyle } from './Block.types';

const Block: FC<BlockProps> = ({
  backgroundColor,
  bottomSpacing,
  children,
  className,
  dir,
  horizontalPadding,
  language,
  maxLines,
  padding,
  paddingBottom,
  paddingLeft,
  paddingRight,
  paddingTop,
  textAlign,
  textColor,
  textSize,
  textWrap,
  tooltip,
  topSpacing,
  verticalPadding,
}) => {
  const theme = useTheme();
  const { inner, block } = getClassNames({
    theme,
    topSpacing,
    bottomSpacing,
    padding,
    horizontalPadding,
    verticalPadding,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    textAlign,
    textColor,
    backgroundColor,
    textSize,
    maxLines,
    textWrap,
    dir,
    className,
  });

  const hasMaxHeight = textSize && maxLines;
  const hasSimpleTextWrap = textWrap && textWrap !== TextWrapStyle.MultiLineEllipsis;
  const hasMultilineEllipsisWithMaxLines = textWrap === TextWrapStyle.MultiLineEllipsis && maxLines && maxLines > 0;
  const content =
    hasMaxHeight || hasSimpleTextWrap || hasMultilineEllipsisWithMaxLines ? (
      <div className={inner}>{children}</div>
    ) : (
      children
    );

  return (
    <div className={block} dir={dir} title={tooltip} lang={language}>
      {content}
    </div>
  );
};

/**
 * A `Block` is a layout component to build consistent padding and vertical spacing between
 * components. It allows you to `push` a chunk of UI up or down by individual pixels to keep text in
 * the `4px` vertical rhythm. It's also the primary place you should set `textSize` in your UIs,
 * providing enumerated options for the supported `font-size`/`line-height` combinations.
 */
export default Block;
